import React, { useEffect, useState, ChangeEvent } from "react";
import axios from "axios";

interface CategoryItem {
  id: number;
  name: string;
  isEditing?: boolean;
  editValue?: string;
}

interface SMTPForm {
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  smtpSSL: boolean;
}

// For Index Price history
interface IndexPriceRecord {
  id: number; // for server-side id, if available
  niftyPrice?: string;
  sensexPrice?: string;
  date: string; // ISO string e.g. 2024-06-13
}

interface Props {}

function Settings(props: Props) {
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [error, setError] = useState("");
  const [catActionLoading, setCatActionLoading] = useState<number | null>(null);

  // --- Form 1: Farm Information ---
  const [farmForm, setFarmForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });
  const [savedFarmForm, setSavedFarmForm] = useState<any>(null);

  // --- Form 2: Stock Index Price History ---
  const [indexPriceForm, setIndexPriceForm] = useState({
    niftyPrice: "",
    sensexPrice: "",
    date: new Date().toISOString().split("T")[0], // yyyy-mm-dd
  });

  const [indexPriceHistory, setIndexPriceHistory] = useState<IndexPriceRecord[]>([]);
  const [editIndexPriceId, setEditIndexPriceId] = useState<number | null>(null);
  const [editIndexPriceRecord, setEditIndexPriceRecord] = useState<IndexPriceRecord | null>(null);

  // --- Form 3: SMTP Setup ---
  const [smtpForm, setSmtpForm] = useState<SMTPForm>({
    smtpHost: "",
    smtpPort: "",
    smtpUser: "",
    smtpPassword: "",
    smtpSSL: false,
  });
  const [savedSmtpForm, setSavedSmtpForm] = useState<any>(null);

  // --- Website Logo state: Will upload to backend, get absolute URL, then save and display ---
  const [websiteLogo, setWebsiteLogo] = useState<string>(""); // for preview and loading from server
  const [savedWebsiteLogo, setSavedWebsiteLogo] = useState<string>("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUploading, setLogoUploading] = useState<boolean>(false);

  // Interest calculation (legacy) states for reference
  const [interestType, setInterestType] = useState<"simple" | "compound">("simple");
  const [percentage, setPercentage] = useState<string>("");
  const [savedInterestType, setSavedInterestType] = useState<string>("");
  const [savedPercentage, setSavedPercentage] = useState<string>("");

  // Helper to get the absolute URL of a logo stored on backend
  const getBackendImageUrl = (relativePath: string | undefined) => {
    if (!relativePath) return "";
    if (
      relativePath.startsWith("http://") ||
      relativePath.startsWith("https://")
    ) {
      return relativePath;
    }
    // Serve from backend static, commonly e.g. [API_URL]/static/uploads/...
    const base = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace(/\/$/, "")
      : "";
    if (relativePath.startsWith("/")) {
      return base + relativePath;
    }
    return base + "/" + relativePath;
  };

  // Load all settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/setting`
        );
        const settingsArray = Array.isArray(res.data) ? res.data : [];

        // Interest (legacy)
        const foundInterestType = settingsArray.find(
          (item: any) =>
            item.meta_key === "interest_type" &&
            (item.meta_value === "simple" || item.meta_value === "compound")
        );
        const foundPercentage = settingsArray.find(
          (item: any) => item.meta_key === "percentage"
        );
        if (foundInterestType) {
          setInterestType(foundInterestType.meta_value as "simple" | "compound");
        }
        if (foundPercentage) {
          setPercentage(foundPercentage.meta_value?.toString() || "");
        }

        // Farm info
        const farm = settingsArray.find(
          (item: any) => item.meta_key === "farm"
        );
        if (farm) {
          const value =
            typeof farm.meta_value === "string"
              ? JSON.parse(farm.meta_value || "{}")
              : farm.meta_value || {};
          setFarmForm({
            name: value.name || "",
            email: value.email || "",
            phone: value.phone || "",
            address: value.address || "",
          });
          setSavedFarmForm(value);
        }

        // Index Price History (array or fallback to old behavior)
        let history: IndexPriceRecord[] = [];
        const priceHistorySetting = settingsArray.find(
          (item: any) => item.meta_key === "index_prices_history"
        );
        if (priceHistorySetting) {
          try {
            const historyArray =
              typeof priceHistorySetting.meta_value === "string"
                ? JSON.parse(priceHistorySetting.meta_value || "[]")
                : priceHistorySetting.meta_value || [];
            if (Array.isArray(historyArray)) {
              history = historyArray
                .map((item, idx) => ({
                  ...item,
                  id: item.id ?? idx, // fallback if no id
                  niftyPrice: item.niftyPrice,
                  sensexPrice: item.sensexPrice,
                  date: item.date,
                }))
                .sort((a, b) => (b.date > a.date ? 1 : -1));
              setIndexPriceHistory(history);
            }
          } catch (err) {
            setIndexPriceHistory([]);
          }
        } else {
          // Fallback: legacy single price object, use as initial entry if exists
          const indexPrice = settingsArray.find(
            (item: any) => item.meta_key === "index_prices"
          );
          if (indexPrice) {
            const value =
              typeof indexPrice.meta_value === "string"
                ? JSON.parse(indexPrice.meta_value || "{}")
                : indexPrice.meta_value || {};
            const fakeDate = new Date().toISOString().split("T")[0];
            const rec: IndexPriceRecord = {
              id: 1,
              niftyPrice: value.niftyPrice,
              sensexPrice: value.sensexPrice,
              date: fakeDate,
            };
            setIndexPriceHistory([rec]);
          }
        }

        // SMTP setup
        const smtp = settingsArray.find(
          (item: any) => item.meta_key === "smtp"
        );
        if (smtp) {
          const value =
            typeof smtp.meta_value === "string"
              ? JSON.parse(smtp.meta_value || "{}")
              : smtp.meta_value || {};
          setSmtpForm({
            smtpHost: value.smtpHost || "",
            smtpPort: value.smtpPort || "",
            smtpUser: value.smtpUser || "",
            smtpPassword: value.smtpPassword || "",
            smtpSSL: !!value.smtpSSL,
          });
          setSavedSmtpForm(value);
        }

        // Website logo, meta_key: "logo" (URL/path to backend upload folder e.g. /uploads/filename.png or a public absolute URL)
        const logoSetting = settingsArray.find(
          (item: any) => item.meta_key === "logo"
        );
        if (logoSetting) {
          setWebsiteLogo(getBackendImageUrl(logoSetting.meta_value));
          setSavedWebsiteLogo(getBackendImageUrl(logoSetting.meta_value));
        } else {
          setWebsiteLogo("");
          setSavedWebsiteLogo("");
        }
      } catch (e) {
        // setError("Failed to fetch settings.");
      }
    };

    fetchSettings();
  }, []);

  // Categories logic (as before)
  useEffect(() => {
    if (showCategoryModal) {
      fetchCategories();
    }
    // eslint-disable-next-line
  }, [showCategoryModal]);

  // GET /categories
  const fetchCategories = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/categories`
      );
      // API returns [{ id, category_name }]
      const categoriesArray: CategoryItem[] = res.data.map((cat: any) => ({
        id: cat.id,
        name: cat.category_name,
      }));
      setCategories(categoriesArray);
    } catch (e) {
      setError("Failed to load categories.");
    }
    setLoading(false);
  };

  const handleOpenModal = () => setShowCategoryModal(true);
  const handleCloseModal = () => setShowCategoryModal(false);

  // POST /categories
  const handleAddCategory = async () => {
    const trimmed = newCategory.trim();
    if (!trimmed) return;
    if (
      categories.some(
        (cat) => cat.name.toLowerCase() === trimmed.toLowerCase()
      )
    )
      return;
    setCatActionLoading(-1);
    setError("");
    try {
      const payload = {
        category_name: trimmed,
      };
      await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/categories`,
        payload
      );
      setNewCategory("");
      fetchCategories();
    } catch (err) {
      setError("Failed to add category.");
    }
    setCatActionLoading(null);
  };

  const handleEditCategory = async (
    cat: CategoryItem,
    newValue: string,
    idx: number
  ) => {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    if (
      categories.some(
        (otherCat) =>
          otherCat.id !== cat.id &&
          otherCat.name.toLowerCase() === trimmed.toLowerCase()
      )
    )
      return;

    setCatActionLoading(cat.id);
    setError("");
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/auth/categories/${cat.id}`,
        { category_name: trimmed }
      );
      fetchCategories();
    } catch (err) {
      setError("Failed to update category.");
    }
    setCatActionLoading(null);
  };

  const handleDeleteCategory = async (cat: CategoryItem) => {
    setCatActionLoading(cat.id);
    setError("");
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/auth/categories/${cat.id}`
      );
      fetchCategories();
    } catch (err) {
      setError("Failed to delete category.");
    }
    setCatActionLoading(null);
  };

  // Edit helpers
  const handleStartEdit = (catId: number) => {
    setCategories((cats) =>
      cats.map((c) =>
        c.id === catId
          ? { ...c, isEditing: true, editValue: c.name }
          : { ...c, isEditing: false }
      )
    );
  };
  const handleEditChange = (catId: number, value: string) => {
    setCategories((cats) =>
      cats.map((c) => (c.id === catId ? { ...c, editValue: value } : c))
    );
  };
  const handleCancelEdit = (catId: number) => {
    setCategories((cats) =>
      cats.map((c) => (c.id === catId ? { ...c, isEditing: false } : c))
    );
  };

  // ---- FORM 1: Save Farm Info ----
  const saveFarmInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/setting`, {
        meta_key: "farm",
        meta_value: JSON.stringify(farmForm),
      });
      setSavedFarmForm(farmForm);
    } catch (err) {
      setError("Failed to save farm information.");
    }
  };

  // ---- FORM 2: Save Index Prices (with date, keep as history) ----
  const saveIndexPrices = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    // Build new entry
    const { niftyPrice, sensexPrice, date } = indexPriceForm;
    if (!niftyPrice && !sensexPrice) return;
    if (!date) return;
    const newRec: IndexPriceRecord = {
      id: Date.now(),
      niftyPrice: niftyPrice || undefined,
      sensexPrice: sensexPrice || undefined,
      date,
    };

    // Merge new record into history and sort descending by date
    const updatedHistory = [
      newRec,
      ...indexPriceHistory,
    ].sort((a, b) => (b.date > a.date ? 1 : b.date < a.date ? -1 : 0));

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/setting`, {
        meta_key: "index_prices_history",
        meta_value: JSON.stringify(updatedHistory),
      });
      setIndexPriceHistory(updatedHistory);

      setIndexPriceForm({
        niftyPrice: "",
        sensexPrice: "",
        date: new Date().toISOString().split("T")[0],
      });
    } catch (err) {
      setError("Failed to save index prices.");
    }
  };

  // ---- Index Price History Edit/Delete handlers ----
  const handleEditIndexPriceRow = (rec: IndexPriceRecord) => {
    setEditIndexPriceId(rec.id);
    setEditIndexPriceRecord({ ...rec });
  };

  const handleCancelEditIndexPrice = () => {
    setEditIndexPriceId(null);
    setEditIndexPriceRecord(null);
  };

  const handleSaveEditIndexPrice = async () => {
    if (!editIndexPriceRecord) return;
    setError("");
    if (
      !(editIndexPriceRecord.niftyPrice || editIndexPriceRecord.sensexPrice) ||
      !editIndexPriceRecord.date
    ) {
      setError("Please fill at least one price and a date.");
      return;
    }
    const updatedHistory = indexPriceHistory
      .map((rec) =>
        rec.id === editIndexPriceRecord.id ? { ...editIndexPriceRecord } : rec
      )
      .sort((a, b) => (b.date > a.date ? 1 : b.date < a.date ? -1 : 0));
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/setting`, {
        meta_key: "index_prices_history",
        meta_value: JSON.stringify(updatedHistory),
      });
      setIndexPriceHistory(updatedHistory);
      setEditIndexPriceId(null);
      setEditIndexPriceRecord(null);
    } catch (err) {
      setError("Failed to save changes.");
    }
  };

  const handleDeleteIndexPrice = async (id: number) => {
    setError("");
    const updatedHistory = indexPriceHistory.filter((rec) => rec.id !== id);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/setting`, {
        meta_key: "index_prices_history",
        meta_value: JSON.stringify(updatedHistory),
      });
      setIndexPriceHistory(updatedHistory);
      if (editIndexPriceId === id) {
        setEditIndexPriceId(null);
        setEditIndexPriceRecord(null);
      }
    } catch (err) {
      setError("Failed to delete price entry.");
    }
  };

  // ---- FORM 3: Save SMTP Settings ----
  const saveSmtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/setting`, {
        meta_key: "smtp",
        meta_value: JSON.stringify(smtpForm),
      });
      setSavedSmtpForm(smtpForm);
    } catch (err) {
      setError("Failed to save SMTP settings.");
    }
  };

  // --- Save Website Logo: Upload to backend (e.g. /uploads or /static/uploads), get URL and save that as meta_value ---
  const handleLogoFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      // For preview (before upload)
      const reader = new FileReader();
      reader.onloadend = () => {
        setWebsiteLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // This upload will POST file to /upload-static (or /upload), backend returns { url }
  const saveWebsiteLogo = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLogoUploading(true);

    if (!logoFile) {
      setError("Please select a logo file.");
      setLogoUploading(false);
      return;
    }

    try {
      // 1. Upload file to backend (e.g. 'uploads' folder)
      const formData = new FormData();
      formData.append("file", logoFile);

      // Endpoint should save to backend uploads and return a url, relative or absolute
      const uploadRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/upload-static`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const { url: logoUrl } = uploadRes.data || {};
      if (!logoUrl) {
        throw new Error("No URL returned from backend upload");
      }

      // 2. Save meta with backend path (relative or absolute URL)
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/setting`, {
        meta_key: "logo",
        meta_value: logoUrl,
      });

      // 3. For preview and future, get absolute path for display
      const absUrl = getBackendImageUrl(logoUrl);
      setSavedWebsiteLogo(absUrl);
      setWebsiteLogo(absUrl); // for preview
      setLogoUploading(false);
    } catch (err) {
      setError("Failed to upload and save website logo.");
      setLogoUploading(false);
    }
  };

  // ---- Interest (legacy, for compatibility, can remove if not needed) ----
  const handleSaveInterest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!percentage.trim()) return;
    setError("");

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/setting`, {
        meta_key: "interest_type",
        meta_value: interestType,
      });
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/setting`, {
        meta_key: "percentage",
        meta_value: percentage.toString(),
      });

      setSavedInterestType(interestType);
      setSavedPercentage(percentage);
    } catch (err) {
      setError("Failed to save settings.");
    }
  };

  // ------- UI -------
  return (
    <>
      <div className="p-6 space-y-8">
        <div>
          <h1 className="text-[28px] text-[#101828] font-[500] mb-1">
            Settings
          </h1>
          <button
            className="bg-[#1CAC70] hover:bg-[#16895a] text-white rounded-[8px] px-4 py-2 font-[500] mt-4"
            onClick={handleOpenModal}
          >
            Manage Categories
          </button>
        </div>

        {/* 1: FARM INFO FORM */}
        <div className="bg-[#ffff] p-5 w-full rounded-[10px]">
          <h2 className="text-lg font-semibold mb-4">Farm Information</h2>
          <form onSubmit={saveFarmInfo}>
            <div className="flex flex-col md:flex-row md:gap-4">
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="farm_name">Farm Name</label>
                <input
                  id="farm_name"
                  type="text"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter farm name"
                  value={farmForm.name}
                  onChange={e =>
                    setFarmForm(f => ({ ...f, name: e.target.value }))
                  }
                />
              </div>
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="farm_email">Farm Email</label>
                <input
                  id="farm_email"
                  type="email"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter farm email"
                  value={farmForm.email}
                  onChange={e =>
                    setFarmForm(f => ({ ...f, email: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:gap-4">
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="farm_phone">Phone Number</label>
                <input
                  id="farm_phone"
                  type="text"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter phone number"
                  value={farmForm.phone}
                  onChange={e =>
                    setFarmForm(f => ({ ...f, phone: e.target.value }))
                  }
                />
              </div>
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="farm_address">Address</label>
                <input
                  id="farm_address"
                  type="text"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter address"
                  value={farmForm.address}
                  onChange={e =>
                    setFarmForm(f => ({ ...f, address: e.target.value }))
                  }
                />
              </div>
            </div>

            <button
              type="submit"
              className="mt-3 bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8 text-white font-[500]"
            >
              Save
            </button>
            {savedFarmForm && (
              <div className="mt-3 bg-[#f6fff6] border border-[#8ce4b6] rounded-[8px] p-3 text-[#16895a] text-[15px]">
                Saved Farm: {savedFarmForm.name} | {savedFarmForm.email} | {savedFarmForm.phone} | {savedFarmForm.address}
              </div>
            )}
          </form>
        </div>

        {/* WEBSITE LOGO SETTING */}
        <div className="bg-[#ffff] p-5 w-full rounded-[10px]">
          <h2 className="text-lg font-semibold mb-4">Website Logo</h2>
          <form onSubmit={saveWebsiteLogo} className="flex flex-col gap-3">
            <div>
              <label htmlFor="website_logo_input">Upload Logo Image</label>
              <input
                id="website_logo_input"
                type="file"
                accept="image/*"
                className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none w-full"
                onChange={handleLogoFileChange}
              />
            </div>
            {websiteLogo && (
              <div className="border p-2 rounded bg-[#f9fafb] flex items-center space-x-4">
                <span className="text-xs text-gray-500">Preview:&nbsp;</span>
                <img
                  src={websiteLogo}
                  alt="Logo preview"
                  style={{
                    height: 40,
                    maxWidth: 180,
                    objectFit: "contain",
                    background: "#fff",
                  }}
                  onError={e => (e.currentTarget.style.display = "none")}
                />
              </div>
            )}
            <button
              type="submit"
              className="bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8 text-white font-[500] mt-2"
              disabled={logoUploading || !logoFile}
            >
              {logoUploading ? "Saving..." : "Save Logo"}
            </button>
            {savedWebsiteLogo && (
              <div className="mt-2 text-[#16895a] text-sm">
                Logo Saved!{" "}
                <a
                  href={savedWebsiteLogo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[#1CAC70] ml-2 text-xs"
                >
                  View Logo
                </a>
              </div>
            )}
          </form>
        </div>

        {/* 2: NIFTY & SENSEX PRICES FORM (with date & history) */}
        <div className="bg-[#ffff] p-5 w-full rounded-[10px]">
          <h2 className="text-lg font-semibold mb-4">
            Index Prices (History)
          </h2>
          <form onSubmit={saveIndexPrices} className="mb-6">
            <div className="flex flex-col md:flex-row md:gap-4 w-full">
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="nifty_price">NIFTY Price</label>
                <input
                  id="nifty_price"
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter NIFTY price"
                  value={indexPriceForm.niftyPrice}
                  onChange={e => {
                    const val = e.target.value;
                    setIndexPriceForm(f => ({
                      ...f,
                      niftyPrice: val,
                    }));
                  }}
                />
              </div>
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="sensex_price">SENSEX Price</label>
                <input
                  id="sensex_price"
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter SENSEX price"
                  value={indexPriceForm.sensexPrice}
                  onChange={e => {
                    const val = e.target.value;
                    setIndexPriceForm(f => ({
                      ...f,
                      sensexPrice: val,
                    }));
                  }}
                />
              </div>
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="index_date">Date</label>
                <input
                  id="index_date"
                  type="date"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  value={indexPriceForm.date}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={e =>
                    setIndexPriceForm(f => ({
                      ...f,
                      date: e.target.value,
                    }))
                  }
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              className="mt-3 bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8 text-white font-[500]"
            >
              Add Price Entry
            </button>
          </form>

          {/* Show saved history */}
          <div className="mt-2">
            <h3 className="font-medium mb-2">Saved Entries:</h3>
            {indexPriceHistory.length === 0 ? (
              <div className="text-gray-400">No index price history.</div>
            ) : (
              <table className="w-full text-sm border rounded-[8px] overflow-x-auto">
                <thead>
                  <tr className="bg-[#f6f8fa]">
                    <th className="px-2 py-1 border">Date</th>
                    <th className="px-2 py-1 border">NIFTY Price</th>
                    <th className="px-2 py-1 border">SENSEX Price</th>
                    <th className="px-2 py-1 border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {indexPriceHistory.map(rec =>
                    editIndexPriceId === rec.id ? (
                      <tr
                        key={
                          rec.id ??
                          `${rec.date}-${rec.niftyPrice}-${rec.sensexPrice}`
                        }
                      >
                        <td className="border px-2 py-1">
                          <input
                            type="date"
                            className="border p-1 rounded"
                            value={editIndexPriceRecord?.date || rec.date}
                            max={new Date().toISOString().split("T")[0]}
                            onChange={e =>
                              setEditIndexPriceRecord(prev =>
                                prev
                                  ? { ...prev, date: e.target.value }
                                  : { ...rec, date: e.target.value }
                              )
                            }
                          />
                        </td>
                        <td className="border px-2 py-1">
                          <input
                            type="number"
                            min="0"
                            className="border p-1 rounded w-[80px]"
                            value={
                              editIndexPriceRecord?.niftyPrice !== undefined
                                ? editIndexPriceRecord.niftyPrice
                                : rec.niftyPrice || ""
                            }
                            onChange={e =>
                              setEditIndexPriceRecord(prev =>
                                prev
                                  ? { ...prev, niftyPrice: e.target.value }
                                  : { ...rec, niftyPrice: e.target.value }
                              )
                            }
                          />
                        </td>
                        <td className="border px-2 py-1">
                          <input
                            type="number"
                            min="0"
                            className="border p-1 rounded w-[80px]"
                            value={
                              editIndexPriceRecord?.sensexPrice !== undefined
                                ? editIndexPriceRecord.sensexPrice
                                : rec.sensexPrice || ""
                            }
                            onChange={e =>
                              setEditIndexPriceRecord(prev =>
                                prev
                                  ? { ...prev, sensexPrice: e.target.value }
                                  : { ...rec, sensexPrice: e.target.value }
                              )
                            }
                          />
                        </td>
                        <td className="border px-2 py-1">
                          <button
                            className="mr-2 px-2 py-1 text-sm bg-[#1CAC70] text-white rounded hover:bg-[#16895a]"
                            style={{ minWidth: 50 }}
                            onClick={handleSaveEditIndexPrice}
                            type="button"
                          >
                            Save
                          </button>
                          <button
                            className="px-2 py-1 text-sm bg-[#ececec] text-[#888] rounded hover:bg-[#d5d5d5]"
                            style={{ minWidth: 60 }}
                            onClick={handleCancelEditIndexPrice}
                            type="button"
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr
                        key={
                          rec.id ??
                          `${rec.date}-${rec.niftyPrice}-${rec.sensexPrice}`
                        }
                      >
                        <td className="border px-2 py-1">{rec.date}</td>
                        <td className="border px-2 py-1">
                          {rec.niftyPrice ?? "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {rec.sensexPrice ?? "-"}
                        </td>
                        <td className="border px-2 py-1">
                          <button
                            className="mr-2 px-2 py-1 text-sm bg-[#f4f5f7] border border-[#ccc] rounded text-[#1CAC70] hover:bg-[#e5eee9]"
                            style={{ minWidth: 50 }}
                            onClick={() => handleEditIndexPriceRow(rec)}
                            type="button"
                          >
                            Edit
                          </button>
                          <button
                            className="px-2 py-1 text-sm bg-[#fff0f0] border border-[#ccc] rounded text-red-500 hover:bg-[#fde2e2]"
                            style={{ minWidth: 60 }}
                            onClick={() => handleDeleteIndexPrice(rec.id)}
                            type="button"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* 3: SMTP SETUP FORM */}
        <div className="bg-[#ffff] p-5 w-full rounded-[10px]">
          <h2 className="text-lg font-semibold mb-4">SMTP Setup</h2>
          <form onSubmit={saveSmtp}>
            <div className="flex flex-col md:flex-row md:gap-4 w-full">
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="smtp_host">SMTP Host</label>
                <input
                  id="smtp_host"
                  type="text"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter SMTP host"
                  value={smtpForm.smtpHost}
                  onChange={e =>
                    setSmtpForm(f => ({ ...f, smtpHost: e.target.value }))
                  }
                />
              </div>
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="smtp_port">SMTP Port</label>
                <input
                  id="smtp_port"
                  type="text"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter SMTP port"
                  value={smtpForm.smtpPort}
                  onChange={e =>
                    setSmtpForm(f => ({ ...f, smtpPort: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:gap-4 w-full">
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="smtp_user">SMTP Username</label>
                <input
                  id="smtp_user"
                  type="text"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter SMTP username"
                  value={smtpForm.smtpUser}
                  onChange={e =>
                    setSmtpForm(f => ({ ...f, smtpUser: e.target.value }))
                  }
                />
              </div>
              <div className="flex-1 flex flex-col mb-2">
                <label htmlFor="smtp_pass">SMTP Password</label>
                <input
                  id="smtp_pass"
                  type="password"
                  className="border-[#D0D5DD] border-[1px] p-2 rounded-[8px] mt-1 outline-none"
                  placeholder="Enter SMTP password"
                  value={smtpForm.smtpPassword}
                  onChange={e =>
                    setSmtpForm(f => ({
                      ...f,
                      smtpPassword: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="flex items-center mt-2 mb-2">
              <input
                id="smtp_ssl"
                type="checkbox"
                checked={smtpForm.smtpSSL}
                onChange={e =>
                  setSmtpForm(f => ({
                    ...f,
                    smtpSSL: e.target.checked,
                  }))
                }
                className="mr-2"
              />
              <label htmlFor="smtp_ssl">Use SSL</label>
            </div>
            <button
              type="submit"
              className="mt-3 bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8 text-white font-[500]"
            >
              Save
            </button>
            {savedSmtpForm && (
              <div className="mt-3 bg-[#f6fff6] border border-[#8ce4b6] rounded-[8px] p-3 text-[#16895a] text-[15px]">
                Saved - Host: {savedSmtpForm.smtpHost}, Port: {savedSmtpForm.smtpPort}, User: {savedSmtpForm.smtpUser}
                , SSL: {savedSmtpForm.smtpSSL ? "Yes" : "No"}
              </div>
            )}
          </form>
        </div>

        {/* Old Interest Form (optional) */}
        {
          <div className="bg-[#ffff] p-5 w-full rounded-[10px] mt-4">
            <form onSubmit={handleSaveInterest}>
              <div className="flex flex-wrap w-full justify-between mt-4 gap-4">
                <div className="w-[100%] md:w-[48%] flex flex-col">
                  <label htmlFor="interestType">Interest Type</label>
                  <select
                    id="interestType"
                    value={interestType}
                    onChange={e =>
                      setInterestType(
                        e.target.value === "compound" ? "compound" : "simple"
                      )
                    }
                    className="w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1 bg-white"
                  >
                    <option value="compound">Compound Interest</option>
                    <option value="simple">Simple Interest</option>
                  </select>
                </div>
                <div className="w-[100%] md:w-[48%] flex flex-col">
                  <label htmlFor="percentage">Percentage (%)</label>
                  <input
                    type="number"
                    id="percentage"
                    name="percentage"
                    placeholder="Enter percentage rate"
                    value={percentage}
                    onChange={e => setPercentage(e.target.value)}
                    className="w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1"
                    min="0"
                    step="any"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="mt-6 flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8"
              >
                Save
              </button>
            </form>
            {(savedInterestType || savedPercentage) && (
              <div className="mt-4 bg-[#f6fff6] border border-[#8ce4b6] rounded-[8px] p-3 text-[#16895a] text-[15px]">
                Saved:{" "}
                {savedInterestType === "compound"
                  ? "Compound Interest"
                  : "Simple Interest"}{" "}
                – {savedPercentage}%
              </div>
            )}
          </div>
        }
        {/* Error display */}
        {error && (
          <div className="mt-2 text-red-500 text-[15px]">{error}</div>
        )}
      </div>

      {/* Categories Modal */}
      {showCategoryModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white w-[95vw] max-w-[440px] rounded-[16px] shadow-md p-7 relative"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center">
              <h2 className="text-[20px] font-[600]">Manage Categories</h2>
              <button
                className="text-[24px] font-bold text-gray-500 hover:text-red-500"
                onClick={handleCloseModal}
              >
                ×
              </button>
            </div>
            {error && <div className="text-red-500 my-2">{error}</div>}
            {loading ? (
              <div className="my-6 text-center text-gray-400">Loading...</div>
            ) : (
              <>
                {/* Add new category */}
                <div className="flex gap-2 mt-4 mb-3">
                  <input
                    type="text"
                    placeholder="Enter new category"
                    value={newCategory}
                    disabled={!!catActionLoading}
                    onChange={e => setNewCategory(e.target.value)}
                    className="flex-1 border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    disabled={!!catActionLoading || !newCategory.trim()}
                    className={`bg-[#1CAC70] text-white rounded-[8px] px-4 py-2 font-[500] ${
                      !!catActionLoading || !newCategory.trim()
                        ? "opacity-50"
                        : "hover:bg-[#16895a]"
                    }`}
                  >
                    {catActionLoading === -1 ? "Adding..." : "Add"}
                  </button>
                </div>

                {/* List all categories */}
                <div className="max-h-[320px] overflow-y-auto mt-3">
                  {categories.length === 0 ? (
                    <div className="text-gray-400 text-center text-[15px]">
                      No categories found.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {categories.map(cat => (
                        <li
                          key={cat.id}
                          className="flex items-center gap-2 bg-[#f8fafb] px-3 py-2 rounded-[8px]"
                        >
                          {cat.isEditing ? (
                            <input
                              className="flex-1 border border-[#D0D5DD] rounded-[7px] px-2 py-1"
                              value={cat.editValue ?? cat.name}
                              autoFocus
                              disabled={!!catActionLoading}
                              onChange={e =>
                                handleEditChange(cat.id, e.target.value)
                              }
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  handleEditCategory(
                                    cat,
                                    cat.editValue ?? "",
                                    cat.id
                                  );
                                } else if (e.key === "Escape") {
                                  handleCancelEdit(cat.id);
                                }
                              }}
                            />
                          ) : (
                            <span className="flex-1 text-[#333] text-[15px]">
                              {cat.name}
                            </span>
                          )}

                          {cat.isEditing ? (
                            <>
                              <button
                                className="text-green-700 font-semibold px-2 hover:underline"
                                disabled={!!catActionLoading}
                                onClick={() =>
                                  handleEditCategory(
                                    cat,
                                    cat.editValue ?? "",
                                    cat.id
                                  )
                                }
                              >
                                Save
                              </button>
                              <button
                                className="text-gray-500 font-semibold px-2 hover:underline"
                                disabled={!!catActionLoading}
                                onClick={() => handleCancelEdit(cat.id)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="text-[#1CAC70] font-semibold px-2 hover:underline"
                                disabled={!!catActionLoading}
                                onClick={() => handleStartEdit(cat.id)}
                              >
                                Edit
                              </button>
                              <button
                                className="text-red-500 font-semibold px-2 hover:underline"
                                disabled={!!catActionLoading}
                                onClick={() => handleDeleteCategory(cat)}
                              >
                                {catActionLoading === cat.id
                                  ? "Deleting..."
                                  : "Delete"}
                              </button>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Settings;

